import React, { useState, useEffect } from 'react';
import { Container, Typography, Paper, Button, Box, Link, Drawer, Table, TableBody, TableCell, CircularProgress, TableContainer, TableHead, TableRow, Divider, useMediaQuery, Modal } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import { Star, StarBorder } from '@mui/icons-material';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import PDF from '../../assets/Images/PDF.svg';
import axios from 'axios';
import './ContentLibrary.css';
import GlobalVariable from "../../services/Global";

const baseApiUrl = GlobalVariable.BASE_API_URL;

const SectionDetails = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const { id } = useParams();
  const [sectionData, setSectionData] = useState({});
  const [versionHistory, setVersionHistory] = useState([]);
  const [isPdfOpen, setPdfOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const isMobile = useMediaQuery('(max-width:600px)');
  const [loading, setLoading] = useState(false);


  const storedData = JSON.parse(localStorage.getItem('userDetails'));
  let token = storedData?.access_token;

  const toggleSidebar = () => {
    setSidebarOpen(!isSidebarOpen);
  };

  const refreshtoken = async () => {
    const userId = storedData.userDetails.id;
    try {
      const data = {
        userid: userId,
      };
      const response = await axios({
        method: "POST",
        url: `${baseApiUrl}/users/refreshtoken`,
        data: JSON.stringify(data),
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (response?.data?.status_code === 200) {
        const { userDetails, access_token } = response.data;

        const userData = {
          userDetails,
          access_token,
        };
        localStorage.setItem("userDetails", JSON.stringify(userData));
        token = access_token;
        window.location.reload();
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchSectionDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseApiUrl}/contentlibrary/getspecific?id=${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.data.status_code === 200) {
        const { data } = response.data;
        setSectionData(data);
        setVersionHistory(data.version_history || []);
        setLoading(false);

      }
    } catch (error) {
      if (error.response?.status === 401) {
        await refreshtoken();
        setLoading(false);

      } else {
        setLoading(false);

      }
    }
  };

  const togglePdfView = () => {
    setPdfOpen(!isPdfOpen);
  };

  const handleVersionClick = (version) => {
    setSelectedVersion(version);
    // Update section data with selected version data
    setSectionData(prevData => ({
      ...prevData,
      content_description: version.content_description,
      star_rating: version.star_rating,
      content_file: version.content_file,
      content_filename: version.content_filename,
      updated_at: version.updated_at,
      toggle: version.toggle,
      comment: version.comment || prevData.comment
    }));
    // Close the sidebar
    setSidebarOpen(false);
  };

  const resetToCurrentVersion = () => {
    setSelectedVersion(null);
    // Reset to original data
    fetchSectionDetails();
  };

  useEffect(() => {
    fetchSectionDetails();
  }, []);

  const Sidebar = ({ onClose }) => (
    <Box p={3} width={isMobile ? '100%' : '400px'} role="presentation">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" className='fontstyleeb' gutterBottom>
          Version History
        </Typography>
        <CloseIcon onClick={onClose} style={{ cursor: 'pointer' }} />
      </Box>
      <Divider sx={{ marginBottom: 1 }} />
      
      {selectedVersion && (
        <Box mb={2}>
          <Button
            variant="outlined"
            size="small"
            onClick={resetToCurrentVersion}
            sx={{ mb: 1 }}
          >
            Back to Current Version
          </Button>
        </Box>
      )}
      
      <TableContainer component={Paper} sx={{ borderColor: 'ActiveBorder', borderRadius: '20px', marginTop: '15px' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell className='fontstyleeb'>S.No</TableCell>
              <TableCell className='fontstyleeb'>Version</TableCell>
              <TableCell className='fontstyleeb'>Updated On</TableCell>
              <TableCell className='fontstyleeb'>Response Source</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {versionHistory && versionHistory.length > 0 ? versionHistory.map((version, index) => (
              <TableRow 
                key={version.version} 
                onClick={() => handleVersionClick(version)}
                sx={{ 
                  cursor: 'pointer',
                  backgroundColor: selectedVersion?.version === version.version ? '#e3f2fd' : 'transparent',
                  '&:hover': { backgroundColor: '#f5f5f5' }
                }}
              >
                <TableCell>{index + 1}</TableCell>
                <TableCell sx={{ 
                  fontWeight: selectedVersion?.version === version.version ? 'bold' : 'normal',
                  color: '#0175FF',
                  textDecoration: 'underline',
                  cursor: 'pointer'
                }}>
                  Version {version.version}
                </TableCell>
                <TableCell>
                  {version.updated_at ? (() => {
                    const dateObj = new Date(version.updated_at);
                    const options = { timeZone: "Asia/Kolkata" };
                    const ist = new Date(dateObj.toLocaleString("en-US", options));
                    const formatted = ist.toLocaleString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true
                    }).replace(/\b(am|pm)\b/g, (match) => match.toUpperCase());
                    return formatted.replace(",", "") + " IST";
                  })() : 'N/A'}
                </TableCell>
                <TableCell sx={{ textTransform: 'capitalize' }}>
                  {version.toggle === 'On' ? 'saved response' : 'AI-generated'}
                </TableCell>
              </TableRow>
            )) : (
              <TableRow>
                <TableCell colSpan={4} style={{ textAlign: 'center' }}>
                  No version history available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider style={{ marginTop: '34px', borderColor: 'black' }} />
      <Row style={{ marginTop: '15px' }}>
        <Col lg={{ span: 3, offset: 9 }}>
          <Button
            variant="contained"
            className='bg-primary text-capitalize rounded-3'
            fullWidth
            onClick={onClose}
          >
            Close
          </Button>
        </Col>
      </Row>
    </Box>
  );


  return (
    <Container className='rfpDtailsContainer' maxWidth="100%">
      <Box mt={2}>
        <Paper elevation={3} style={{ padding: 20, borderRadius: '15px' }}>
          <Box display="flex" justifyContent="space-between">
            <Box>
              <Typography className='fontstyleeb' variant="h5" gutterBottom>
                Section Details
              </Typography>
              {selectedVersion && (
                <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                  Version {selectedVersion.version} 
                </Typography>
              )}
            </Box>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{ marginRight: 2, color: '#429bf5', borderColor: '#429bf5' }}
              className='buttonsd border-primary text-capitalize rounded-3'
            >
              Back
            </Button>
          </Box>
          <Box className='d-flex mb-4 mt-2' sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
            {loading && (
              <Box
                className="circular-progress"
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 1000,
                }}
              >
                <CircularProgress className=" text-primary" />
              </Box>
            )}
            <Box sx={{ marginBottom: { xs: 2, sm: 0 } }}>
              <Typography className='fontstylee' sx={{ fontSize: 12 }}>Section Name</Typography>
              <Typography sx={{ fontSize: 12 }}>{sectionData && sectionData?.content_name}</Typography>
            </Box>
            <Box sx={{ marginBottom: { xs: 2, sm: 0 }, marginLeft: { xs: 0, sm: 4 } }}>
              <Typography className='fontstylee' variant="subtitle1" sx={{ fontSize: 12 }}>
                Last Updated{' '}
                <Link onClick={toggleSidebar} className='link-underline' style={{ cursor: 'pointer', color: '#0175FF', fontSize: 12 }}>
                  Version History
                </Link>
              </Typography>
              <Typography variant="subtitle1" sx={{ fontSize: 12 }}>
                {sectionData.updated_at ? (() => {
                  const dateObj = new Date(sectionData.updated_at);
                  const options = { timeZone: "Asia/Kolkata" };
                  const ist = new Date(dateObj.toLocaleString("en-US", options));
                  const formatted = ist.toLocaleString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true
                  }).replace(/\b(am|pm)\b/g, (match) => match.toUpperCase());
                  return formatted.replace(",", "") + " IST";
                })() : (sectionData.last_updated || 'N/A')}
              </Typography>
            </Box>
            <Box sx={{ marginBottom: { xs: 2, sm: 0 }, marginLeft: { xs: 0, sm: 4 } }}>
              <Typography className='fontstylee' variant="subtitle1" sx={{ fontSize: 12 }}>
                RFP Type
              </Typography>
              <Typography variant="subtitle1" sx={{ fontSize: 12, textTransform: 'capitalize' }}>
                {sectionData.rfp_type || 'N/A'}
              </Typography>
            </Box>
            <Box sx={{ marginBottom: { xs: 2, sm: 0 }, marginLeft: { xs: 0, sm: 4 } }}>
              <Typography className='fontstylee' variant="subtitle1" sx={{ fontSize: 12 }}>
                Rating
              </Typography>
              <Box display="flex" alignItems="center">
                {[1, 2, 3, 4, 5].map((starIndex) => {
                  const rating = Number(sectionData?.star_rating) || 0;
                  return starIndex <= rating
                    ? <Star key={starIndex} sx={{ color: '#FFD700', fontSize: 20 }} />
                    : <StarBorder key={starIndex} sx={{ color: '#C0C0C0', fontSize: 20 }} />
                })}
              </Box>
            </Box>
            <Box sx={{ marginBottom: { xs: 2, sm: 0 }, marginLeft: { xs: 0, sm: 4 } }}>
              <Typography className='fontstylee' variant="subtitle1" sx={{ fontSize: 12 }}>
                Added By
              </Typography>
              <Typography variant="subtitle1" sx={{ fontSize: 12 }}>
                {sectionData.user_name}
              </Typography>
            </Box>
            <Box sx={{ marginBottom: { xs: 2, sm: 0 }, marginLeft: { xs: 0, sm: 4 } }}>
              <Typography className='fontstylee' variant="subtitle1" sx={{ fontSize: 12 }}>
                Response Source
              </Typography>
              <Typography variant="subtitle1" sx={{ fontSize: 12, textTransform: 'capitalize' }}>
                { sectionData.toggle === 'On' ? 'saved response' : 'AI-generated'}
              </Typography>
            </Box>
          </Box>
          <Typography className='fontstylee' gutterBottom>
            Description
          </Typography>
          <Typography
            variant="body1"
            paragraph
            sx={{ textAlign: "justify", fontSize: 12 }}
            dangerouslySetInnerHTML={{ __html: sectionData.content_description }}
          />
          <Typography className='fontstylee' gutterBottom sx={{ mt: 2 }}>
            Comment
          </Typography>
          <Typography
            variant="body1"
            paragraph
            sx={{ 
              textAlign: "justify", 
              fontSize: 12,
              whiteSpace: 'pre-wrap',
              minHeight: '20px',
              color: sectionData.comment ? 'text.primary' : 'text.secondary'
            }}
          >
            {sectionData.comment || 'No comment added'}
          </Typography>

          <Box alignItems="center" mb={2}>
            {sectionData.content_file ? (
              <>
                <Typography variant="h6" gutterBottom>
                  Attachments
                </Typography>
                <Typography variant="body1" style={{ color: '#0175FF'}}
                >{sectionData?.content_filename}</Typography>
                <img src={PDF} alt="PDF Icon" style={{ cursor: 'pointer', color: '#0175FF', marginLeft: 10 }}
                  onClick={togglePdfView} />
                <Typography variant="body1" style={{ cursor: 'pointer', color: '#0175FF', marginLeft: '10px', marginTop: '8px' }}
                  className='link-underline' onClick={togglePdfView}>View File</Typography>
              </>
            ) : (
              <Typography variant="body1"></Typography>
            )}
          </Box>
          <Box display="flex" justifyContent="flex-end">
            <Button variant="outlined" onClick={() => navigate(-1)} className='border-primary text-capitalize rounded-3' sx={{ marginRight: 2, color: '#0D6EFD' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => navigate(`/edit-section-details/${id}`, { state: { sectionName: sectionData.content_name, description: sectionData.content_description, attachment: sectionData.content_file, filename: sectionData?.content_filename, star_rating: sectionData?.star_rating, rfp_type: sectionData?.rfp_type, version: sectionData?.version, toggle: sectionData?.toggle, comment: sectionData?.comment } })}
              sx={{ bgcolor: '#429bf5' }}
              className='bg-primary text-capitalize rounded-3'
            >
              Edit
            </Button>
          </Box>
        </Paper>
      </Box>
      <Drawer anchor="right" open={isSidebarOpen} onClose={toggleSidebar}>
        <Sidebar onClose={toggleSidebar} />
      </Drawer>
      <Modal open={isPdfOpen} onClose={togglePdfView}>
  <Box
    width={isMobile ? "100%" : "80%"}
    height={isMobile ? "100%" : "80%"}
    margin="auto"
    bgcolor="white"
    display="flex"
    flexDirection="column"
    alignItems="center"
    borderRadius={2}
    justifyContent="center"
    mt={isMobile ? 0 : "5%"}
    p={isMobile ? 1 : 2}
    sx={{
      overflowY: isMobile ? "scroll" : "hidden",
    }}
  >
    <CloseIcon
      onClick={togglePdfView}
      style={{
        alignSelf: "flex-end",
        cursor: "pointer",
        marginBottom: "10px",
        marginRight: isMobile ? "10px" : "0",
      }}
    />
    {sectionData.content_file && (
      <iframe
        src={sectionData.content_file}
        width="100%"
        height="100%"
        title="PDF Viewer"
        style={{
          border: "none",
          margin: 0,
          padding: 0,
        }}
      />
    )}
  </Box>
</Modal>

    </Container>
  );
};

export default SectionDetails;